import Fuse from "fuse.js";
import Breadcrumbs from "../../styles/components/Breadcrumbs";
import { useMemo, useState, useId, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { navigateToTop, scrollToTop } from "../../utils/Constants";
import { useHelpArticles } from "../../hooks/useHelpArticles";
import Loading from "../Loading";
import "../../styles/pages/help/_help.scss";

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconSearch = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <circle
            cx="6.5"
            cy="6.5"
            r="4.5"
            stroke="currentColor"
            strokeWidth="1.4"
        />
        <path
            d="M10 10l3.5 3.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
        />
    </svg>
);

const IconClear = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M3 3l8 8M11 3L3 11"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
        />
    </svg>
);

const IconArrow = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2.5 7h9M8 3.5L11.5 7 8 10.5"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconExternal = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M7 1h4v4M11 1L5.5 6.5M4 3H2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V8"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ─── Category icons ───────────────────────────────────────────────────────────
// const CATEGORY_ICONS = {
//     Learn: "◎",
//     "Practice & Interviews": "▶",
//     Account: "◈",
//     Technical: "⊞",
//     Legal: "▤",
//     Contact: "◉",
//     Other: "◇",
// };

const CATEGORY_ORDER = [
    "Learn",
    "Practice & Interviews",
    "Account",
    "Technical",
    "Legal",
    "Contact",
    "Other",
];

// ─── Article item ─────────────────────────────────────────────────────────────
function ArticleItem({ item, index }) {
    const isMail = String(item.path).startsWith("mailto:");
    const isExternal =
        item.path.startsWith("http://") || item.path.startsWith("https://");
    const isAway = isMail || isExternal;

    const inner = (
        <>
            <div className="hp-item__body">
                <p className="hp-item__title">{item.title}</p>
                {item.description && (
                    <p className="hp-item__desc">{item.description}</p>
                )}
            </div>
            <span className="hp-item__arrow" aria-hidden="true">
                {isAway ? <IconExternal /> : <IconArrow />}
            </span>
        </>
    );

    const sharedProps = {
        className: "hp-item__link",
        style: { "--item-index": index },
    };

    return (
        <li className="hp-item">
            {isAway ? (
                <a
                    href={item.path}
                    {...sharedProps}
                    target="_blank"
                    rel="noreferrer"
                    onClick={navigateToTop}
                >
                    {inner}
                </a>
            ) : (
                <Link to={item.path} {...sharedProps} onClick={navigateToTop}>
                    {inner}
                </Link>
            )}
        </li>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Help() {
    const tabsId = useId();
    const searchInputRef = useRef(null);
    const { articles, loading } = useHelpArticles();

    const helpArticles = useMemo(() => {
        const extras = [
            {
                id: "faqs",
                title: "FAQs",
                description: "Find quick answers to the most common questions.",
                path: "/faqs",
                category: "Learn",
            },
            // {
            //     id: "privacy",
            //     title: "Privacy policy",
            //     description:
            //         "How we collect, use, and protect your data and recordings.",
            //     path: "/privacy-policy",
            //     category: "Legal",
            // },
            // {
            //     id: "terms",
            //     title: "Terms of service",
            //     description:
            //         "Your rights and responsibilities when using Mooz.",
            //     path: "/terms-of-service",
            //     category: "Legal",
            // },
            // {
            //     id: "cookies",
            //     title: "Cookies policy",
            //     description:
            //         "What we store, why we store it, and how to manage your preferences.",
            //     path: "/cookies",
            //     category: "Legal",
            // },
            {
                id: "contact",
                title: "Contact us",
                description:
                    "Email us and we'll help you get back to practising quickly.",
                path: "/contact",
                category: "Contact",
            },
            // {
            //     id: "",
            //     title: "",
            //     description:
            //         "",
            //     path: "",
            //     category: "",
            // },
        ];

        const combined = [...articles, ...extras].filter(
            (a) => a.title && a.path && a.category
        );

        combined.sort((a, b) => {
            const ai = CATEGORY_ORDER.indexOf(a.category);
            const bi = CATEGORY_ORDER.indexOf(b.category);
            const aR = ai === -1 ? 999 : ai;
            const bR = bi === -1 ? 999 : bi;
            if (aR !== bR) return aR - bR;
            return a.title.localeCompare(b.title);
        });

        return combined;
    }, [articles]);

    const fuse = useMemo(
        () =>
            new Fuse(helpArticles, {
                keys: ["title", "description", "category", "content"],
                threshold: 0.33,
                ignoreLocation: true,
                minMatchCharLength: 2,
            }),
        [helpArticles]
    );

    const [query, setQuery] = useState("");
    const [activeCategory, setActiveCategory] = useState("All");

    const categories = useMemo(() => {
        const set = new Set(helpArticles.map((a) => a.category));
        return ["All", ...Array.from(set)];
    }, [helpArticles]);

    const filtered = useMemo(() => {
        const base = query
            ? fuse.search(query).map((r) => r.item)
            : helpArticles;
        return activeCategory === "All"
            ? base
            : base.filter((a) => a.category === activeCategory);
    }, [query, fuse, helpArticles, activeCategory]);

    const groupedByCategory = useMemo(() => {
        const map = new Map();
        filtered.forEach((item) => {
            const key = item.category || "Other";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(item);
        });
        const entries = Array.from(map.entries());
        entries.sort((a, b) => {
            const ai = CATEGORY_ORDER.indexOf(a[0]);
            const bi = CATEGORY_ORDER.indexOf(b[0]);
            const aR = ai === -1 ? 999 : ai;
            const bR = bi === -1 ? 999 : bi;
            if (aR !== bR) return aR - bR;
            return a[0].localeCompare(b[0]);
        });
        return entries.map(([category, items]) => ({ category, items }));
    }, [filtered]);

    // Keyboard shortcut
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
            }
            if (
                e.key === "Escape" &&
                document.activeElement === searchInputRef.current
            ) {
                setQuery("");
                searchInputRef.current?.blur();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    return (
        <>
            <div className="hp" id="help">
                <div className="container">
                    {/* ── Hero ── */}
                    <section className="hp-hero">
                        <div className="hp-hero__inner">
                            <Breadcrumbs
                                items={[
                                    { label: "Home", href: "/" },
                                    { label: "Help", href: "/help" },
                                ]}
                            />

                            <h1 className="hp-hero__title">
                                Interview prep is confusing.
                                <br />
                                <em>We have answers.</em>
                            </h1>
                            <p className="hp-hero__sub">
                                Browse our guides, policies, and support
                                resources below.
                            </p>

                            {/* Search */}
                            {/* <div className="hp-search" role="search">
                                <span className="hp-search__icon">
                                    <IconSearch />
                                </span>
                                <label
                                    className="sr-only"
                                    htmlFor="help-search-input"
                                >
                                    Search help
                                </label>
                                <input
                                    id="help-search-input"
                                    ref={searchInputRef}
                                    type="search"
                                    className="hp-search__input"
                                    placeholder="Search help articles…"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    aria-label="Search help articles"
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                                {query ? (
                                    <button
                                        className="hp-search__clear"
                                        onClick={() => {
                                            setQuery("");
                                            searchInputRef.current?.focus();
                                        }}
                                        aria-label="Clear search"
                                        type="button"
                                    >
                                        <IconClear />
                                    </button>
                                ) : (
                                    <kbd
                                        className="hp-search__kbd"
                                        aria-hidden="true"
                                    >
                                        <span>⌘</span>K
                                    </kbd>
                                )}
                            </div> */}

                            {/* Results meta */}
                            {/* {query && (
                                <p
                                    className="hp-search__meta"
                                    aria-live="polite"
                                >
                                    <strong>{filtered.length}</strong> result
                                    {filtered.length !== 1 ? "s" : ""} for
                                    &ldquo;
                                    {query}&rdquo;
                                </p>
                            )} */}
                        </div>
                    </section>

                    {/* ── Category tabs ── */}
                    <div className="hp-tabs-wrap">
                        <nav
                            className="hp-tabs"
                            role="tablist"
                            aria-label="Help categories"
                            id={tabsId}
                        >
                            {categories.map((c) => {
                                const active = activeCategory === c;
                                return (
                                    <button
                                        key={c}
                                        type="button"
                                        className={`hp-tab${active ? " is-active" : ""}`}
                                        role="tab"
                                        aria-selected={active}
                                        onClick={() => {
                                            setActiveCategory(c);
                                            scrollToTop();
                                        }}
                                    >
                                        {/* {c !== "All" && CATEGORY_ICONS[c] && (
                                            <span
                                                className="hp-tab__icon"
                                                aria-hidden="true"
                                            >
                                                {CATEGORY_ICONS[c]}
                                            </span>
                                        )} */}
                                        {c}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>

                    {/* ── Content ── */}
                    <div className="hp-content">
                        {filtered.length === 0 ? (
                            <div className="hp-empty" role="status">
                                <div
                                    className="hp-empty__icon"
                                    aria-hidden="true"
                                >
                                    <IconSearch />
                                </div>
                                <p className="hp-empty__title">
                                    No results{query ? ` for "${query}"` : ""}
                                </p>
                                <p className="hp-empty__body">
                                    Try a different keyword or switch
                                    categories.
                                </p>
                                {(query || activeCategory !== "All") && (
                                    <button
                                        type="button"
                                        className="hp-empty__reset"
                                        onClick={() => {
                                            setQuery("");
                                            setActiveCategory("All");
                                        }}
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </div>
                        ) : activeCategory === "All" ? (
                            // All categories: grouped sections
                            <div className="hp-sections">
                                {groupedByCategory.map((group) => (
                                    <section
                                        className="hp-section"
                                        key={group.category}
                                        aria-label={group.category}
                                    >
                                        <div className="hp-section__head">
                                            {/* <span
                                                className="hp-section__icon"
                                                aria-hidden="true"
                                            >
                                                {CATEGORY_ICONS[
                                                    group.category
                                                ] || "◇"}
                                            </span> */}
                                            <h2 className="hp-section__title">
                                                {group.category}
                                            </h2>
                                            <span className="hp-section__count">
                                                {group.items.length}
                                            </span>
                                        </div>
                                        <ul className="hp-list" role="list">
                                            {group.items.map((item, i) => (
                                                <ArticleItem
                                                    key={`${group.category}-${item.id}`}
                                                    item={item}
                                                    index={i}
                                                />
                                            ))}
                                        </ul>
                                    </section>
                                ))}
                            </div>
                        ) : (
                            // Single category view
                            <section
                                className="hp-single"
                                aria-label={activeCategory}
                            >
                                <div className="hp-single__head">
                                    {/* <span
                                        className="hp-single__icon"
                                        aria-hidden="true"
                                    >
                                        {CATEGORY_ICONS[activeCategory] || "◇"}
                                    </span> */}
                                    <h2 className="hp-single__title">
                                        {activeCategory}
                                    </h2>
                                    <span className="hp-section__count">
                                        {filtered.length}
                                    </span>
                                </div>
                                <ul className="hp-list" role="list">
                                    {filtered.map((item, i) => (
                                        <ArticleItem
                                            key={item.id}
                                            item={item}
                                            index={i}
                                        />
                                    ))}
                                </ul>
                            </section>
                        )}
                    </div>
                </div>
            </div>

            {loading && <Loading />}
        </>
    );
}
